import * as z from 'zod/mini'
import { keyedIssue } from '../../errors/validation-keys'
import { emailField, queryNumber } from '../../schemas/common'

/**
 * One version of a template (`admin/email-templates/:templateId/:locale/:version`). The template id
 * and locale are plain strings, not UUIDs; `version` arrives as a string and must be a positive
 * integer, or the request is a 404 like any unknown version.
 */
export const emailTemplateVersionParams = z.object({
  templateId: z.string().check(z.minLength(1)),
  locale: z.string().check(z.minLength(1)),
  version: queryNumber(z.int().check(z.positive())),
})
export type EmailTemplateVersionParams = z.infer<typeof emailTemplateVersionParams>

/** The latest version of a template in one locale (`admin/email-templates/:templateId/:locale`). */
export const emailTemplateLocaleParams = z.object({
  templateId: z.string().check(z.minLength(1)),
  locale: z.string().check(z.minLength(1)),
})
export type EmailTemplateLocaleParams = z.infer<typeof emailTemplateLocaleParams>

/** A template carries exactly one body: MJML (compiled on send) or raw HTML. Reported on `html`. */
const exactlyOneBody = <T extends { mjml?: string; html?: string }>(value: T, ctx: z.core.$RefinementCtx<T>) => {
  if (!value.mjml === !value.html) {
    ctx.addIssue(keyedIssue('validation.mjmlXorHtml', ['html']))
  }
}

export const createEmailTemplateBody = z
  .object({
    templateId: z.string().check(z.minLength(1)),
    locale: z.string().check(z.minLength(1)),
    subject: z.string(),
    mjml: z.optional(z.string()),
    html: z.optional(z.string()),
  })
  .check(z.superRefine(exactlyOneBody))
export type CreateEmailTemplateBody = z.infer<typeof createEmailTemplateBody>

export const sendTestEmailBody = z
  .object({
    subject: z.string(),
    targetEmail: emailField,
    variables: z.record(z.string(), z.string()),
    mjml: z.optional(z.string()),
    html: z.optional(z.string()),
  })
  .check(z.superRefine(exactlyOneBody))
export type SendTestEmailBody = z.infer<typeof sendTestEmailBody>
